import { Button, Spinner, TextInput } from "flowbite-react";
import { AnimatePresence, motion } from "framer-motion";
import { Icon, X } from "@phosphor-icons/react";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AnimatedModalProps {
  show: boolean;
  onClose: () => void;
  title?: string;
  icon?: Icon;
  children?: ReactNode;
  footer?: ReactNode;
  closeButton?: boolean;
  isConfirmation?: boolean;
  onConfirm?: () => void;
  confirmText?: string;
  isConfirming?: boolean;
  cancelText?: string;
  confirmButtonColor?:
  | "red"
  | "blue"
  | "green"
  | "indigo"
  | "purple"
  | "yellow"
  | "orange"
  | "pink"
  | "gray"
  | "teal"
  | "lime"
  | "fuchsia"
  | "violet"
  | "cyan"
  | "emerald";
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "full" | "half";
  disableBackdropClick?: boolean;
  bodyClassName?: string;
  confirmationText?: string;
  confirmationTypeLabel?: string | ReactNode;
}

// Maps confirmButtonColor to Spinner color
const spinnerColorMap: Record<string, string> = {
  red: "failure",
  blue: "info",
  green: "success",
  yellow: "warning",
  pink: "pink",
  purple: "purple",
  indigo: "purple", // fallback to info
  orange: "warning", // fallback to warning
  gray: "gray", // fallback to info
  teal: "info", // fallback to info
  lime: "success", // fallback to success
  fuchsia: "pink", // fallback to pink
  violet: "purple", // fallback to purple
  cyan: "info", // fallback to info
  emerald: "success", // fallback to success
};

function getSpinnerColor(confirmButtonColor: string) {
  return spinnerColorMap[confirmButtonColor] || "info";
}

const AnimatedModal = ({
  show,
  onClose,
  title,
  icon: Icon,
  children,
  footer,
  closeButton = true,
  isConfirmation = false,
  onConfirm,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmButtonColor = "blue",
  size = "md",
  disableBackdropClick = false,
  isConfirming = false,
  bodyClassName,
  confirmationText,
  confirmationTypeLabel,
}: AnimatedModalProps) => {
  const { t } = useTranslation();
  const [userConfirmation, setUserConfirmation] = useState("");

  useEffect(() => {
    if (!show) setUserConfirmation("");
  }, [show]);

  const getModalSize = () => {
    switch (size) {
      case "sm":
        return "sm:max-w-sm";
      case "lg":
        return "sm:max-w-2xl";
      case "xl":
        return "sm:max-w-4xl";
      case "2xl":
        return "sm:max-w-6xl";
      case "full":
        return "w-full h-full sm:max-w-none sm:h-auto";
      case "half":
        return "w-full sm:max-w-2xl h-1/2";
      default:
        return "sm:max-w-lg";
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !disableBackdropClick) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Restore body scroll when modal is closed
      document.body.style.overflow = "unset";
    };
  }, [show, onClose, disableBackdropClick]);

  return (
    <AnimatePresence>
      {show && (
        <div
          className="fixed inset-0 z-50"
          onClick={!disableBackdropClick ? onClose : undefined}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-gray-900/40"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{
              duration: 0.15,
              type: "spring",
              damping: 25,
              stiffness: 300,
            }}
            className="fixed inset-0 overflow-y-auto flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              exit={{ y: 20 }}
              transition={{ duration: 0.15 }}
              className={`relative transform  rounded-xl bg-white shadow-2xl transition-all sm:w-full ${getModalSize()}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              {closeButton && (
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-full p-1 transition-colors"
                >
                  <X size={20} />
                </button>
              )}

              {/* Header */}
              {title && (
                <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                  <div className="flex items-center justify-center gap-2">
                    {Icon && <Icon className="w-5 h-5 text-gray-500" />}
                    <h3 className={`text-lg font-semibold text-gray-600`}>
                      {t(title, title)}
                    </h3>
                  </div>
                </div>
              )}

              {/* Body */}
              <div className={`${bodyClassName || "px-6 py-3"}`}>
                {children}
              </div>

              {/* Footer */}
              {isConfirmation ? (
                <>
                  {confirmationText && (
                    <div className="w-full mb-4 flex flex-col items-center">
                      <label className="text-sm text-gray-600 mb-2">
                        {confirmationTypeLabel || (
                          <>
                            {t("common.confirmAction", { text: confirmationText })}
                          </>
                        )}
                      </label>
                      <TextInput
                        type="text"
                        value={userConfirmation}
                        onChange={(e) => setUserConfirmation(e.target.value)}
                        onPaste={(e) => e.preventDefault()}
                        onDrop={(e) => e.preventDefault()}
                      />
                    </div>
                  )}
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 sm:flex sm:flex-row-reverse sm:gap-3 justify-center rounded-b-xl">
                    <Button
                      type="button"
                      color={confirmButtonColor}
                      onClick={onConfirm}
                      processingSpinner={
                        <Spinner
                          size="sm"
                          color={getSpinnerColor(confirmButtonColor)}
                          className="-mt-[2px]"
                        />
                      }
                      isProcessing={isConfirming}
                      disabled={
                        (!!confirmationText &&
                          userConfirmation !== confirmationText) ||
                        isConfirming
                      }
                    >
                      <div className="flex items-center">{t(confirmText || "common.confirm", confirmText)}</div>
                    </Button>
                    <Button
                      type="button"
                      color={"light"}
                      onClick={onClose}
                      disabled={isConfirming}
                    >
                      <div className="flex items-center"> {t(cancelText || "common.cancel", cancelText)}</div>
                    </Button>
                  </div>
                </>
              ) : (
                footer && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 sm:flex sm:flex-row-reverse sm:gap-3 rounded-b-xl">
                    {footer}
                  </div>
                )
              )}
            </motion.div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AnimatedModal;
